import {
  ContentState,
  EditorState,
  convertFromHTML as draftConvertFromHTML,
  convertToRaw,
} from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import React from 'react';
import { Editor } from 'react-draft-wysiwyg';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import TextAreaAutosize from 'react-textarea-autosize';

import ID from 'react-form-builder2/lib/UUID';
import DynamicOptionList from './DynamicOptionList';
import MultiSelectOptionsEditor from './MultiSelectOptionsEditor';
import AddressElementsEditor from './AddressElementsEditor';
import IntlMessages from 'react-form-builder2/lib/language-provider/IntlMessages';
import { get } from 'react-form-builder2/lib/stores/requests';
import { CRM_SOURCE_LIST, getCrmSource } from '../crm';
import PaymentProviderEditor from './PaymentProviderEditor';
import PaymentOrderSettings from './PaymentOrderSettings';
import AiGeneratorBlocksEditor from './AiGeneratorBlocksEditor';
import SlotBookingEditor from './slotBookingEditor/SlotBookingEditor';
import QuizBlockEditor from './quizBlocksEditor/QuizBlockEditor';
import { QUIZ_BLOCK_KEYS } from '../CustomBlocks/quiz/quizTypes';
import { SCALE_BLOCK_KEYS } from '../CustomBlocks/scale/scaleTypes';
import ScaleBlockEditor from './scaleBlocksEditor/ScaleBlockEditor';
import { resolveAiConfig, resolveRespondentEditable } from '../CustomBlocks/aiGenerator/aiConfig';
import {
  CHIP_SELECT_LAYOUTS,
  canAutoAdvance,
  layoutShowsImages,
} from '../CustomBlocks/chipSelect/chipSelectTypes';
import { ADMIN_COLORS } from '../../theme';

const toolbar = {
  options: [
    'inline',
    'list',
    'textAlign',
    // 'fontSize',
    'link',
    'history',
  ],
  inline: {
    inDropdown: false,
    className: undefined,
    options: [
      'bold',
      'italic',
      'underline',
      'superscript',
      'subscript',
    ],
  },
};


const RICH_OPTION_ELEMENTS = ['ChipSelect'];

interface FormElementsEditProps {
  element: any;
  data?: any;
  files?: any[];
  manualEditModeOff?: () => void;
  updateElement?: (element: any) => void;
  preview?: any;
  showCorrectColumn?: boolean;
  className?: string;
  /** The form's own field names, for the AI Generator block editor's `{{name}}` insert helper. */
  formFieldNames?: string[];
}

interface FormElementsEditState {
  element: any;
  data: any;
  dirty: boolean;
  showModal: boolean;
  editorKey: number;
  optionsVersion: number;
}

interface FileOption {
  id: string;
  file_name: string;
}

interface FileTypeOption {
  type: string;
  typeName: string;
}

type ButtonType = 'button' | 'reset' | 'submit';

export default class FormElementsEdit extends React.Component<
  FormElementsEditProps,
  FormElementsEditState
> {
  constructor(props: FormElementsEditProps) {
    super(props);
    this.state = {
      element: this.props.element,
      data: this.props.data || {},
      dirty: false,
      showModal: false,
      editorKey: 0,
      optionsVersion: 0,
    };
  }

  static defaultProps = {
    className: 'edit-element-fields',
    files: [],
    showCorrectColumn: false,
  };

  toggleRequired(): void {
    // const this_element = this.state.element;
  }

  editButtonType(
    e: React.ChangeEvent<HTMLSelectElement>,
  ): void {
    const this_element = this.state.element;
    if (!this_element.data) this_element.data = {};
    this_element.data.type = e.target.value as ButtonType;
    if (e.target.value === 'goToWebinar') {
      this_element.data.href = 'https://webinar-fab-builder.web.app/?w={{wid}}&type=webinar&l={{id}}';
      this_element.data.openInNewTab = false;
      this_element.content = 'Go To Webinar';
    }
    this.setState(
      { element: this_element, dirty: true, editorKey: this.state.editorKey + 1 },
      () => {
        this.updateElement();
      },
    );
  }

  editButtonClass(
    e: React.ChangeEvent<HTMLSelectElement>,
  ): void {
    const this_element = this.state.element;
    if (!this_element.data) this_element.data = {};
    const val = e.target.value;
    this_element.data.className = val;
    this_element.className = val;
    this.setState(
      { element: this_element, dirty: true },
      () => {
        this.updateElement();
      },
    );
  }

  editElementProp(
    elemProperty: string,
    targProperty: string,
    e: React.ChangeEvent<
      | HTMLInputElement
      | HTMLTextAreaElement
      | HTMLSelectElement
    >,
  ): void {
    const this_element = this.state.element;
    const target = e.target as any;
    this_element[elemProperty] = target[targProperty];
    this.setState(
      {
        element: this_element,
        dirty: true,
      },
      () => {
        // Push every keystroke/selection to the canvas, not just checkboxes, so
        // the preview reflects the panel live instead of only on panel close.
        this.updateElement();
      },
    );
  }

  onEditorStateChange(
    _index: number,
    property: string,
    editorContent: EditorState,
  ): void {
    const html = draftToHtml(
      convertToRaw(editorContent.getCurrentContent()),
    )
      .replace(/<p>/g, '')
      .replace(/<\/p>/g, '')
      .replace(/&nbsp;/g, ' ')
      .replace(/(?:\r\n|\r|\n)/g, ' ');
    const this_element = this.state.element;
    this_element[property] = html;

    this.setState({ element: this_element, dirty: true }, () => {
      this.updateElement();
    });
  }

  updateElement(): void {
    const this_element = this.state.element;
    if (this.state.dirty) {
      if (typeof this.props.updateElement === 'function') {
        try {
          this.props.updateElement(this_element);
        } catch (e) {
          try {
            this.props.updateElement.call(
              this.props.preview,
              this_element,
            );
          } catch (err) {}
        }
      }
      this.setState({ dirty: false });
    }
  }

  handleBlur = () => {
    if (this.state.dirty) {
      this.updateElement();
    }
  };

  convertFromHTMLContent(content: string): EditorState {
    const newContent = draftConvertFromHTML(content);
    if (
      !newContent.contentBlocks ||
      !newContent.contentBlocks.length
    ) {
      // to prevent crash when no contents in editor
      return EditorState.createEmpty();
    }
    const contentState = ContentState.createFromBlockArray(
      newContent as any,
    );
    return EditorState.createWithContent(contentState);
  }

  addOptions(): void {
    const optionsApiUrlInput = document.getElementById(
      'optionsApiUrl',
    ) as HTMLInputElement;
    const optionsApiUrl = optionsApiUrlInput?.value?.trim();
    if (!optionsApiUrl) return;

    // The library's get() has no error path — a bad URL, a non-array
    // response, or a network failure previously failed completely silently.
    get(optionsApiUrl)
      .then((data: any) => {
        if (!Array.isArray(data)) {
          // eslint-disable-next-line no-console
          console.error(
            'Populate Options from API: expected a JSON array, got:',
            data,
          );
          window.alert(
            'That endpoint did not return a JSON array of options. Check the browser console for the actual response.',
          );
          return;
        }

        const this_element = this.state.element;
        if (!this_element || !this_element.hasOwnProperty('options')) return;

        this_element.options = data.map((x: any) => ({
          ...x,
          key: ID.uuid(),
        }));
        this.setState(
          {
            element: this_element,
            dirty: true,
            // Forces DynamicOptionList to remount and pick up the replaced
            // options — it never re-syncs from new props on its own.
            optionsVersion: this.state.optionsVersion + 1,
          },
          () => this.updateElement(),
        );
      })
      .catch((err: any) => {
        // eslint-disable-next-line no-console
        console.error('Populate Options from API: request failed.', err);
        window.alert(
          'Could not load options from that URL. Check the browser console for details.',
        );
      });
  }
  render(): JSX.Element {
    if (this.state.dirty) {
      this.props.element.dirty = true;
    }

    const element =
      this.state.element || this.props.element;

    const this_checked = this.props.element.hasOwnProperty(
      'required',
    )
      ? this.props.element.required
      : false;
    const this_read_only =
      this.props.element.hasOwnProperty('readOnly')
        ? this.props.element.readOnly
        : false;
    const this_default_today =
      this.props.element.hasOwnProperty('defaultToday')
        ? this.props.element.defaultToday
        : false;
    const this_show_time_select =
      this.props.element.hasOwnProperty('showTimeSelect')
        ? this.props.element.showTimeSelect
        : false;
    const this_show_time_select_only =
      this.props.element.hasOwnProperty(
        'showTimeSelectOnly',
      )
        ? this.props.element.showTimeSelectOnly
        : false;
    const this_show_time_input =
      this.props.element.hasOwnProperty('showTimeInput')
        ? this.props.element.showTimeInput
        : false;
    const this_checked_inline =
      this.props.element.hasOwnProperty('inline')
        ? this.props.element.inline
        : false;
    const this_checked_center =
      this.props.element.hasOwnProperty('center')
        ? this.props.element.center
        : false;
    const this_checked_page_break =
      this.props.element.hasOwnProperty('pageBreakBefore')
        ? this.props.element.pageBreakBefore
        : false;
    const {
      canHavePageBreakBefore,
      canHaveDisplayHorizontal,
      canHaveOptionCorrect,
      canHaveOptionValue,
    } = this.props.element;
    const canHaveImageSize =
      this.state.element?.element === 'Image' ||
      this.state.element?.element === 'Camera';

    const this_files: FileOption[] = this.props.files
      ?.length
      ? this.props.files!
      : [];
    if (
      this_files.length < 1 ||
      (this_files.length > 0 && this_files[0]?.id !== '')
    ) {
      this_files.unshift({ id: '', file_name: '' });
    }

    let editorState: EditorState | undefined;
    if (this.props.element.hasOwnProperty('content')) {
      editorState = this.convertFromHTMLContent(
        this.state.element?.content ?? this.props.element.content,
      );
    }
    if (this.props.element.hasOwnProperty('label')) {
      editorState = this.convertFromHTMLContent(
        this.state.element?.label ?? this.props.element.label,
      );
    }
    const isCustomButton =
      this.props.element?.key === 'CustomButton';
    const isPaymentSection =
      this.props.element?.key === 'PaymentSection';
    const isKycButton =
      this.props.element?.key === 'KycButton';
    const isESignButton =
      this.props.element?.key === 'ESignButton';
    const isEmailInput =
      this.props.element?.key === 'EmailInput';
    const isPhoneInput =
      this.props.element?.key === 'PhoneInput';
    const isNameInput =
      this.props.element?.key === 'NameInput';
    const isNumberInput =
      this.props.element?.key === 'NumberInput';
    const isAddressInput =
      this.props.element?.key === 'AddressInput';
    const isAiGenerator =
      this.props.element?.key === 'AiGenerator';
    const isCrmAutocomplete =
      this.props.element?.key === 'CrmAutocomplete';
    const isWorkflowPreview =
      this.props.element?.key === 'WorkflowPreview';
    const isSlotBooking =
      this.props.element?.key === 'SlotBooking';
    const isQuizBlock = QUIZ_BLOCK_KEYS.includes(this.props.element?.key);
    const isScaleBlock = SCALE_BLOCK_KEYS.includes(this.props.element?.key);
    const isTextField = ['TextInput', 'TextArea'].includes(
      this.props.element?.key,
    );
    const isDateTimeInput = [
      'DateInput',
      'TimeInput',
      'DateTimeInput',
      'MonthYearInput',
    ].includes(this.props.element?.key);

    const isStaticContent = Boolean(
      this.state.element?.static ?? this.props.element?.static,
    );

    const currentFieldName = String(
      this.state.element?.field_name ?? this.props.element?.field_name ?? '',
    ).trim();
    const siblingFields: any[] = Array.isArray(this.props.preview?.state?.data)
      ? this.props.preview.state.data
      : [];
    const isDuplicateName =
      Boolean(currentFieldName) &&
      siblingFields.some(
        (field: any) =>
          field &&
          field.id !== element?.id &&
          !field.static &&
          String(field.field_name ?? '').trim() === currentFieldName,
      );

    return (
      <div>
        {!isStaticContent && !isSlotBooking && (
          <div className="form-group">
            <label className="control-label">Field Name</label>
            <input
              type="text"
              className="form-control w-50"
              style={
                isDuplicateName
                  ? { borderColor: '#d32f2f' }
                  : undefined
              }
              value={
                this.state.element?.field_name ?? this.props.element?.field_name ?? ''
              }
              onChange={(e) => {
                const this_element = this.state.element;
                const val = (e.target as HTMLInputElement).value;
                if (!this_element.data) this_element.data = {} as any;
                this_element.field_name = val;
                this.setState({ element: this_element, dirty: true }, () =>
                  this.updateElement(),
                );
              }}
              onBlur={this.updateElement.bind(this)}
            />
            {isDuplicateName ? (
              <small
                className="form-text"
                style={{ color: '#d32f2f' }}
              >
                Another field already uses “{currentFieldName}”. Names must be
                unique — otherwise one answer overwrites the other.
              </small>
            ) : (
              <small className="form-text text-muted">
               The name should be unique for all the fields as it will be used as the key when submitting the form data.
              </small>
            )}
          </div>
        )}


        {this.props.element.hasOwnProperty('content') && (
          <div className="form-group">
            <label className="control-label">
              <IntlMessages id="text-to-display" />:
            </label>

            <Editor
              key={this.state.editorKey}
              toolbar={toolbar}
              defaultEditorState={editorState}
              onBlur={this.updateElement.bind(this)}
              onEditorStateChange={this.onEditorStateChange.bind(
                this,
                0,
                'content',
              )}
              stripPastedStyles={true}
            />
          </div>
        )}
        {isPaymentSection && (
          <div className="alert alert-info" role="alert">
            Place payment on the final step. For automatic CRM lead details, name form fields exactly <code>firstName</code>, <code>lastName</code>, <code>email</code>, <code>phone</code>, and <code>state</code>. Any missing values are collected before payment.
          </div>
        )}
        {isKycButton && (
          <div className="alert alert-info" role="alert">
            PAN and date of birth are collected from the user when the button is clicked. For automatic CRM lead details, name form fields exactly <code>firstName</code>, <code>lastName</code>, <code>email</code>, <code>phone</code>, and <code>state</code>. Any missing values are collected before KYC.
          </div>
        )}
        {isKycButton && (
          <div className="form-group">
            <div className="custom-control custom-checkbox">
              <input
                id="kycIsRequired"
                className="custom-control-input"
                type="checkbox"
                checked={Boolean(this.state.element?.data?.isRequired ?? this.props.element?.data?.isRequired)}
                onChange={(e) => {
                  const this_element = this.state.element;
                  if (!this_element.data) this_element.data = {};
                  this_element.data.isRequired = (e.target as HTMLInputElement).checked;
                  this.setState({ element: this_element, dirty: true }, () => this.updateElement());
                }}
              />
              <label className="custom-control-label" htmlFor="kycIsRequired">
                Is required
              </label>
            </div>
            <small className="form-text text-muted">The form cannot be submitted until KYC is completed.</small>
          </div>
        )}
        {isEmailInput && (
          <div className="form-group">
            <div className="custom-control custom-checkbox">
              <input
                id="showIcon"
                className="custom-control-input"
                type="checkbox"
                checked={Boolean(this.state.element?.showIcon ?? this.props.element?.showIcon ?? true)}
                onChange={(e) => {
                  const this_element = this.state.element;
                  this_element.showIcon = (e.target as HTMLInputElement).checked;
                  this.setState({ element: this_element, dirty: true }, () => this.updateElement());
                }}
              />
              <label className="custom-control-label" htmlFor="showIcon">
                Show icon
              </label>
            </div>
            <small className="form-text text-muted">
              Show a leading envelope icon inside the input.
            </small>
          </div>
        )}
        {(isEmailInput || isPhoneInput) && (
          <div className="form-group">
            <div className="custom-control custom-checkbox">
              <input
                id="requireOtp"
                className="custom-control-input"
                type="checkbox"
                checked={Boolean(this.state.element?.requireOtp ?? this.props.element?.requireOtp)}
                onChange={(e) => {
                  const this_element = this.state.element;
                  this_element.requireOtp = (e.target as HTMLInputElement).checked;
                  this.setState({ element: this_element, dirty: true }, () => this.updateElement());
                }}
              />
              <label className="custom-control-label" htmlFor="requireOtp">
                Require OTP verification
              </label>
            </div>
            <small className="form-text text-muted">
              On submit, the user must verify this {isEmailInput ? 'email' : 'phone number'} with an OTP before the form is submitted.
            </small>
          </div>
        )}
        {isAddressInput && (
          <AddressElementsEditor
            elements={
              this.state.element?.addressElements ??
              this.props.element?.addressElements
            }
            showElementsLabel={Boolean(
              this.state.element?.showElementsLabel ??
                this.props.element?.showElementsLabel ??
                true,
            )}
            showIcon={Boolean(
              this.state.element?.showIcon ??
                this.props.element?.showIcon ??
                true,
            )}
            onChange={(addressElements) => {
              const this_element = this.state.element;
              this_element.addressElements = addressElements;
              this.setState({ element: this_element, dirty: true }, () =>
                this.updateElement(),
              );
            }}
            onShowElementsLabelChange={(show) => {
              const this_element = this.state.element;
              this_element.showElementsLabel = show;
              this.setState({ element: this_element, dirty: true }, () =>
                this.updateElement(),
              );
            }}
            onShowIconChange={(show) => {
              const this_element = this.state.element;
              this_element.showIcon = show;
              this.setState({ element: this_element, dirty: true }, () =>
                this.updateElement(),
              );
            }}
          />
        )}
        {(isNameInput || isEmailInput || isNumberInput || isTextField || isAiGenerator || isCrmAutocomplete) && (
          <div className="form-group">
            <label className="control-label" htmlFor="fieldPlaceholder">
              Placeholder
            </label>
            <input
              id="fieldPlaceholder"
              type="text"
              className="form-control"
              value={
                this.state.element?.placeholder ??
                this.props.element?.placeholder ??
                ''
              }
              onChange={(e) => {
                const this_element = this.state.element;
                this_element.placeholder = (e.target as HTMLInputElement).value;
                this.setState({ element: this_element, dirty: true }, () =>
                  this.updateElement(),
                );
              }}
              onBlur={this.updateElement.bind(this)}
            />
            <small className="form-text text-muted">
              Hint text shown inside the input while it is empty.
            </small>
          </div>
        )}
        {(isAiGenerator || isWorkflowPreview) && (
          <div className="form-group">
            <label className="control-label" htmlFor="aiButtonText">
              Button Text
            </label>
            <input
              id="aiButtonText"
              type="text"
              className="form-control"
              value={
                this.state.element?.buttonText ??
                this.props.element?.buttonText ??
                (isWorkflowPreview ? 'Preview' : 'Generate')
              }
              onChange={(e) => {
                const this_element = this.state.element;
                this_element.buttonText = (e.target as HTMLInputElement).value;
                this.setState({ element: this_element, dirty: true }, () =>
                  this.updateElement(),
                );
              }}
              onBlur={this.updateElement.bind(this)}
            />
            <small className="form-text text-muted">
              {isWorkflowPreview
                ? 'Text displayed on the preview button.'
                : 'Text displayed on the generate button.'}
            </small>
          </div>
        )}
        {isCrmAutocomplete && (() => {
          const sourceKey =
            this.state.element?.crmSource ??
            this.props.element?.crmSource ??
            'segment';
          const sourceDef = getCrmSource(sourceKey);
          const filters =
            this.state.element?.crmFilters ??
            this.props.element?.crmFilters ??
            {};
          return (
            <>
              <div className="form-group">
                <label className="control-label" htmlFor="crmSource">
                  Search in
                </label>
                <select
                  id="crmSource"
                  className="form-control"
                  value={sourceDef.key}
                  onChange={(e) => {
                    const this_element = this.state.element;
                    this_element.crmSource = (e.target as HTMLSelectElement).value;
                    // Filters belong to one source, so a new source starts clean.
                    this_element.crmFilters = {};
                    this.setState({ element: this_element, dirty: true }, () =>
                      this.updateElement(),
                    );
                  }}
                >
                  {CRM_SOURCE_LIST.map((source) => (
                    <option key={source.key} value={source.key}>
                      {source.label}
                    </option>
                  ))}
                </select>
                <small className="form-text text-muted">
                  The CRM list this field lets people pick from. The submitted
                  value is the selected item's id.
                </small>
              </div>
              {sourceDef.filters.map((filter) => (
                <div className="form-group" key={`${sourceDef.key}-${filter.key}`}>
                  <label className="control-label" htmlFor={`crmFilter-${filter.key}`}>
                    {filter.label}
                  </label>
                  <input
                    id={`crmFilter-${filter.key}`}
                    type="text"
                    className="form-control"
                    placeholder={filter.placeholder}
                    value={filters[filter.key] ?? ''}
                    onChange={(e) => {
                      const this_element = this.state.element;
                      this_element.crmFilters = {
                        ...(this_element.crmFilters || {}),
                        [filter.key]: (e.target as HTMLInputElement).value,
                      };
                      this.setState({ element: this_element, dirty: true }, () =>
                        this.updateElement(),
                      );
                    }}
                    onBlur={this.updateElement.bind(this)}
                  />
                  {filter.help && (
                    <small className="form-text text-muted">{filter.help}</small>
                  )}
                </div>
              ))}
            </>
          );
        })()}
        {isAiGenerator && (
          <div className="form-group">
            <AiGeneratorBlocksEditor
              blocks={this.state.element?.blocks ?? this.props.element?.blocks ?? []}
              outputFormat={
                this.state.element?.outputFormat ?? this.props.element?.outputFormat ?? 'markdown'
              }
              mainInputLabel={
                this.state.element?.mainInputLabel ?? this.props.element?.mainInputLabel ?? ''
              }
              aiConfig={resolveAiConfig(
                this.state.element?.aiConfig ?? this.props.element?.aiConfig,
              )}
              respondentEditable={resolveRespondentEditable({
                respondentEditable:
                  this.state.element?.respondentEditable ?? this.props.element?.respondentEditable,
                respondentConfigurable:
                  this.state.element?.respondentConfigurable ??
                  this.props.element?.respondentConfigurable,
              })}
              prompts={
                this.state.element?.prompts ?? this.props.element?.prompts ?? []
              }
              formFieldNames={this.props.formFieldNames ?? []}
              onChange={({ blocks, outputFormat, mainInputLabel, aiConfig, respondentEditable, prompts }) => {
                const this_element = this.state.element;
                this_element.blocks = blocks;
                this_element.outputFormat = outputFormat;
                this_element.mainInputLabel = mainInputLabel;
                this_element.aiConfig = aiConfig;
                this_element.respondentEditable = respondentEditable;
                this_element.prompts = prompts;
                // "Description from another field" mode used to make the field
                // bare (no wrapper label); the field always renders its own
                // textarea now, so the wrapper label applies again.
                this_element.bare = false;
                this.setState({ element: this_element, dirty: true }, () =>
                  this.updateElement(),
                );
              }}
            />
          </div>
        )}
        {(isNameInput || isNumberInput || isDateTimeInput) && (
          <div className="form-group">
            <div className="custom-control custom-checkbox">
              <input
                id="showIcon"
                className="custom-control-input"
                type="checkbox"
                checked={Boolean(this.state.element?.showIcon ?? this.props.element?.showIcon ?? true)}
                onChange={(e) => {
                  const this_element = this.state.element;
                  this_element.showIcon = (e.target as HTMLInputElement).checked;
                  this.setState({ element: this_element, dirty: true }, () => this.updateElement());
                }}
              />
              <label className="custom-control-label" htmlFor="showIcon">
                Show icon
              </label>
            </div>
            <small className="form-text text-muted">
              Show a leading icon inside the input.
            </small>
          </div>
        )}
        {isESignButton && (
          <div className="alert alert-info" role="alert">
            For automatic CRM lead details, name form fields exactly <code>firstName</code>, <code>lastName</code>, <code>email</code>, <code>phone</code>, and <code>state</code>. Any missing values are collected before signing.
          </div>
        )}
        {isESignButton && (
          <div className="form-group">
            <label className="control-label" htmlFor="esignDocumentUrl">
              Document URL
            </label>
            <input
              id="esignDocumentUrl"
              type="text"
              className="form-control"
              value={
                this.state.element?.data?.documentUrl ??
                this.props.element?.data?.documentUrl ??
                ''
              }
              onChange={(e) => {
                const this_element = this.state.element;
                if (!this_element.data) this_element.data = {};
                this_element.data.documentUrl = (e.target as HTMLInputElement).value;
                this.setState({ element: this_element, dirty: true }, () =>
                  this.updateElement(),
                );
              }}
              onBlur={this.updateElement.bind(this)}
            />
            <small className="form-text text-muted">Publicly accessible URL of the PDF document to be signed.</small>
          </div>
        )}
        {isESignButton && (
          <div className="form-group">
            <div className="custom-control custom-checkbox">
              <input
                id="esignIsRequired"
                className="custom-control-input"
                type="checkbox"
                checked={Boolean(this.state.element?.data?.isRequired ?? this.props.element?.data?.isRequired)}
                onChange={(e) => {
                  const this_element = this.state.element;
                  if (!this_element.data) this_element.data = {};
                  this_element.data.isRequired = (e.target as HTMLInputElement).checked;
                  this.setState({ element: this_element, dirty: true }, () => this.updateElement());
                }}
              />
              <label className="custom-control-label" htmlFor="esignIsRequired">
                Is required
              </label>
            </div>
            <small className="form-text text-muted">The form cannot be submitted until the document is signed.</small>
          </div>
        )}
        {isPaymentSection && (
          <div className="form-group">
            <div className="custom-control custom-checkbox">
              <input
                id="paymentIsRequired"
                className="custom-control-input"
                type="checkbox"
                checked={Boolean(this.state.element?.data?.isRequired ?? this.props.element?.data?.isRequired)}
                onChange={(e) => {
                  const this_element = this.state.element;
                  if (!this_element.data) this_element.data = {};
                  this_element.data.isRequired = (e.target as HTMLInputElement).checked;
                  this.setState({ element: this_element, dirty: true }, () => this.updateElement());
                }}
              />
              <label className="custom-control-label" htmlFor="paymentIsRequired">
                Is required
              </label>
            </div>
            <small className="form-text text-muted">The form cannot be submitted until this payment is completed.</small>
          </div>
        )}
        {isPaymentSection && (
          <PaymentProviderEditor
            providers={this.state.element?.data?.providers ?? this.props.element?.data?.providers}
            paymentMode={this.state.element?.data?.paymentMode ?? this.props.element?.data?.paymentMode}
            planOptions={this.state.element?.data?.planOptions ?? this.props.element?.data?.planOptions}
            gstRate={this.state.element?.data?.gstRate ?? this.props.element?.data?.gstRate}
            planApi={this.state.element?.data?.planApi ?? this.props.element?.data?.planApi}
            onChange={(patch) => {
              const this_element = this.state.element;
              if (!this_element.data) this_element.data = {};
              Object.assign(this_element.data, patch);
              this.setState({ element: this_element, dirty: true }, () =>
                this.updateElement(),
              );
            }}
          />
        )}
        {isPaymentSection && (
          <PaymentOrderSettings
            config={{ ...(this.props.element?.data || {}), ...(this.state.element?.data || {}) }}
            isPlanMode={(this.state.element?.data?.paymentMode ?? this.props.element?.data?.paymentMode) === 'plan'}
            onChange={(patch) => {
              const this_element = this.state.element;
              if (!this_element.data) this_element.data = {};
              Object.assign(this_element.data, patch);
              this.setState({ element: this_element, dirty: true }, () =>
                this.updateElement(),
              );
            }}
          />
        )}
        {isPaymentSection && !((this.state.element?.data?.paymentMode ?? this.props.element?.data?.paymentMode) === 'plan') && (
          <div className="form-group">
            <label className="control-label" htmlFor="paymentAmount">
              Amount
            </label>
            <input
              id="paymentAmount"
              type="number"
              className="form-control w-50"
              value={
                this.state.element?.data?.amount ??
                this.props.element?.data?.amount ??
                ''
              }
              onChange={(e) => {
                const this_element = this.state.element;
                if (!this_element.data) this_element.data = {};
                this_element.data.amount = (e.target as HTMLInputElement).value;
                this.setState({ element: this_element, dirty: true }, () =>
                  this.updateElement(),
                );
              }}
              onBlur={this.updateElement.bind(this)}
            />
          </div>
        )}
        {isPaymentSection && (
          <div className="form-group">
            <label className="control-label">
              Button Alignment
            </label>
            <select
              className="form-control w-50"
              value={
                this.state.element?.data?.className ||
                this.props.element?.data?.className ||
                ''
              }
              onChange={this.editButtonClass.bind(this)}
              onBlur={this.updateElement.bind(this)}
            >
              {' '}
              <option value="btn d-flex mr-auto">
                Left
              </option>
              <option value="btn d-flex mx-auto">
                Center
              </option>
              <option value="btn d-flex ml-auto">
                Right
              </option>
            </select>
          </div>
        )}
        {isPaymentSection && (
          <div className="form-group">
            <label className="control-label" htmlFor="paymentDescription">
              Description
            </label>
            <input
              id="paymentDescription"
              type="text"
              className="form-control"
              value={
                this.state.element?.data?.description ||
                this.props.element?.data?.description ||
                ''
              }
              onChange={(e) => {
                const this_element = this.state.element;
                if (!this_element.data) this_element.data = {};
                this_element.data.description = (e.target as HTMLInputElement).value;
                this.setState({ element: this_element, dirty: true }, () =>
                  this.updateElement(),
                );
              }}
              onBlur={this.updateElement.bind(this)}
            />
          </div>
        )}
        {isPaymentSection && !((this.state.element?.data?.paymentMode ?? this.props.element?.data?.paymentMode) === 'plan') && (
          <div className="form-group">
            <label className="control-label" htmlFor="paymentDiscountMode">
              Discount Mode
            </label>
            <select
              id="paymentDiscountMode"
              className="form-control w-50"
              value={
                this.state.element?.data?.discountMode ||
                this.props.element?.data?.discountMode ||
                'none'
              }
              onChange={(e) => {
                const this_element = this.state.element;
                if (!this_element.data) this_element.data = {};
                this_element.data.discountMode = (e.target as HTMLSelectElement).value;
                this.setState({ element: this_element, dirty: true }, () =>
                  this.updateElement(),
                );
              }}
              onBlur={this.updateElement.bind(this)}
            >
              <option value="none">None</option>
              <option value="percentage">Percentage</option>
              <option value="flat">Flat</option>
            </select>
          </div>
        )}
        {isPaymentSection &&
          !((this.state.element?.data?.paymentMode ?? this.props.element?.data?.paymentMode) === 'plan') &&
          (this.state.element?.data?.discountMode ||
            this.props.element?.data?.discountMode) &&
          (this.state.element?.data?.discountMode ||
            this.props.element?.data?.discountMode) !== 'none' && (
            <div className="form-group">
              <label className="control-label" htmlFor="paymentDiscountValue">
                {(this.state.element?.data?.discountMode ||
                  this.props.element?.data?.discountMode) === 'flat'
                  ? 'Discount Amount'
                  : 'Discount %'}
              </label>
              <input
                id="paymentDiscountValue"
                type="number"
                className="form-control w-50"
                value={
                  this.state.element?.data?.discountValue ??
                  this.props.element?.data?.discountValue ??
                  ''
                }
                onChange={(e) => {
                  const this_element = this.state.element;
                  if (!this_element.data) this_element.data = {};
                  this_element.data.discountValue = (e.target as HTMLInputElement).value;
                  this.setState({ element: this_element, dirty: true }, () =>
                    this.updateElement(),
                  );
                }}
                onBlur={this.updateElement.bind(this)}
              />
            </div>
          )}
        {isPaymentSection && !((this.state.element?.data?.paymentMode ?? this.props.element?.data?.paymentMode) === 'plan') && (
          <div className="form-group">
            <label className="control-label" htmlFor="paymentGstMode">
              GST Mode
            </label>
            <select
              id="paymentGstMode"
              className="form-control w-50"
              value={
                this.state.element?.data?.gstMode ||
                this.props.element?.data?.gstMode ||
                'none'
              }
              onChange={(e) => {
                const this_element = this.state.element;
                if (!this_element.data) this_element.data = {};
                this_element.data.gstMode = (e.target as HTMLSelectElement).value;
                this.setState({ element: this_element, dirty: true }, () =>
                  this.updateElement(),
                );
              }}
              onBlur={this.updateElement.bind(this)}
            >
              <option value="none">None</option>
              <option value="included">Included</option>
              <option value="excluded">Excluded</option>
            </select>
          </div>
        )}
        {isPaymentSection && !((this.state.element?.data?.paymentMode ?? this.props.element?.data?.paymentMode) === 'plan') && (
          <div className="form-group">
            <label className="control-label" htmlFor="paymentBreakdownDisplay">
              Breakdown Display
            </label>
            <select
              id="paymentBreakdownDisplay"
              className="form-control w-50"
              value={
                this.state.element?.data?.breakdownDisplay ||
                this.props.element?.data?.breakdownDisplay ||
                'inline'
              }
              onChange={(e) => {
                const this_element = this.state.element;
                if (!this_element.data) this_element.data = {};
                this_element.data.breakdownDisplay = (e.target as HTMLSelectElement).value;
                this.setState({ element: this_element, dirty: true }, () =>
                  this.updateElement(),
                );
              }}
              onBlur={this.updateElement.bind(this)}
            >
              <option value="inline">Inline in form</option>
              <option value="dialog">Dialog on click</option>
              <option value="off">Off</option>
            </select>
          </div>
        )}

        {(isCustomButton || isPaymentSection || isKycButton || isESignButton) && (
          <div className="form-group">
            <label className="control-label">
              Button Type
            </label>
            <select
              className="form-control w-50"
              value={
                this.state.element?.data?.type ||
                this.props.element?.data?.type ||
                'button'
              }
              onChange={this.editButtonType.bind(this)}
              onBlur={this.updateElement.bind(this)}
            >
              <option value="button">Button</option>
              <option value="submit">Submit</option>
              <option value="reset">Reset</option>
              <option value="nextStep">Next Step</option>
              <option value="prevStep">Previous Step</option>
              <option value="goToWebinar">Go To Webinar</option>
            </select>
          </div>
        )}

        {(isCustomButton || isKycButton || isESignButton) && (
          <div className="form-group">
            <label className="control-label">
              Button Alignment
            </label>
            <select
              className="form-control w-50"
              value={
                this.state.element?.data?.className ||
                this.props.element?.data?.className ||
                ''
              }
              onChange={this.editButtonClass.bind(this)}
              onBlur={this.updateElement.bind(this)}
            >
              {' '}
              <option value="btn d-flex mr-auto">
                Left
              </option>
              <option value="btn d-flex mx-auto">
                Center
              </option>
              <option value="btn d-flex ml-auto">
                Right
              </option>
            </select>
          </div>
        )}
        {(isCustomButton || isPaymentSection || isKycButton || isESignButton) && (
          <div className="form-group">
            <label
              className="control-label"
              htmlFor="submitCallbackFn"
            >
              Callback Function
            </label>
            <textarea
              id="submitCallbackFn"
              className="form-control"
              placeholder="Enter callback function name or code"
              value={
                this.state.element?.data
                  ?.onSubmitCallback ||
                this.props.element?.data
                  ?.onSubmitCallback ||
                ''
              }
              onChange={(e) => {
                const this_element = this.state.element;
                if (!this_element.data)
                  this_element.data = {};
                this_element.data.onSubmitCallback =
                  e.target.value;
                this.setState(
                  {
                    element: this_element,
                    dirty: true,
                  },
                  () => this.updateElement(),
                );
              }}
              onBlur={this.updateElement.bind(this)}
            />
            <small className="form-text text-muted">
              This function will be called when the submit
              button is clicked.
            </small>
          </div>
        )}
        {(isCustomButton || isPaymentSection || isKycButton || isESignButton) && (
          <div className="form-group">
            <label className="control-label">Link</label>
            <TextAreaAutosize
              className="form-control"
              value={
                this.state.element?.data?.href ||
                this.props.element?.data?.href ||
                ''
              }
              onChange={(e) => {
                const this_element = this.state.element;
                if (!this_element.data) this_element.data = {};
                this_element.data.href=
                  (e.target as HTMLTextAreaElement).value;
                this.setState({ element: this_element, dirty: true }, () =>
                  this.updateElement(),
                );
              }}
              onBlur={this.updateElement.bind(this)}
            />
            <small className="form-text text-muted">
              Use placeholders like {'{{id}}'}; these
              will be replaced with the server response value
              after submission.
            </small>
          </div>
        )}

        {(isCustomButton || isPaymentSection || isKycButton || isESignButton) && (
          <div className="form-group">
            <div className="custom-control custom-checkbox">
              <input
                id="openInNewTab"
                className="custom-control-input"
                type="checkbox"
                checked={Boolean(
                  this.state.element?.data?.openInNewTab ??
                    this.props.element?.data?.openInNewTab,
                )}
                onChange={(e) => {
                  const this_element = this.state.element;
                  if (!this_element.data) this_element.data = {};
                  this_element.data.openInNewTab = (
                    e.target as HTMLInputElement
                  ).checked;
                  this.setState({ element: this_element, dirty: true }, () => {
                    this.updateElement();
                  });
                }}
              />
              <label
                className="custom-control-label"
                htmlFor="openInNewTab"
              >
                Open link in new tab
              </label>
            </div>
          </div>
        )}

        {this.props.element.hasOwnProperty('file_path') && (
          <div className="form-group">
            <label
              className="control-label"
              htmlFor="fileSelect"
            >
              <IntlMessages id="choose-file" />:
            </label>
            <select
              id="fileSelect"
              className="form-control"
              defaultValue={this.props.element.file_path}
              onBlur={this.updateElement.bind(this)}
              onChange={this.editElementProp.bind(
                this,
                'file_path',
                'value',
              )}
            >
              {this_files.map((file: FileOption) => {
                const this_key = `file_${file.id}`;
                return (
                  <option value={file.id} key={this_key}>
                    {file.file_name}
                  </option>
                );
              })}
            </select>
          </div>
        )}
        {this.props.element.hasOwnProperty('href') && (
          <div className="form-group">
            <TextAreaAutosize
              className="form-control"
              defaultValue={this.props.element.href}
              onBlur={this.updateElement.bind(this)}
              onChange={this.editElementProp.bind(
                this,
                'href',
                'value',
              )}
            />
          </div>
        )}
        {this.props.element.hasOwnProperty('label') && (
          <div className="form-group">
            <label>
              <IntlMessages id="display-label" />
            </label>
            <Editor
              toolbar={toolbar}
              defaultEditorState={editorState}
              onBlur={this.updateElement.bind(this)}
              onEditorStateChange={this.onEditorStateChange.bind(
                this,
                0,
                'label',
              )}
              stripPastedStyles={true}
            />
            <br />
            <div className="custom-control custom-checkbox">
              <input
                id="is-required"
                className="custom-control-input"
                type="checkbox"
                checked={this_checked}
                onChange={this.editElementProp.bind(
                  this,
                  'required',
                  'checked',
                )}
              />
              <label
                className="custom-control-label"
                htmlFor="is-required"
              >
                <IntlMessages id="required" />
              </label>
            </div>

            {this.props.element.key === 'ChipSelect' && (
              <div className="custom-control custom-checkbox">
                <input
                  id="allow-multiple"
                  className="custom-control-input"
                  type="checkbox"
                  checked={Boolean(this.props.element.multiple)}
                  onChange={this.editElementProp.bind(
                    this,
                    'multiple',
                    'checked',
                  )}
                />
                <label className="custom-control-label" htmlFor="allow-multiple">
                  Allow multiple selections
                </label>
              </div>
            )}
      
            {this.props.element.key === 'ChipSelect' && (
              <div className="custom-control custom-checkbox">
                <input
                  id="allow-custom-value"
                  className="custom-control-input"
                  type="checkbox"
                  checked={Boolean(this.props.element.allowCustomValue)}
                  onChange={this.editElementProp.bind(
                    this,
                    'allowCustomValue',
                    'checked',
                  )}
                />
                <label
                  className="custom-control-label"
                  htmlFor="allow-custom-value"
                >
                  Allow a custom value
                </label>
              </div>
            )}
            {this.props.element.key === 'ChipSelect' &&
              Boolean(this.props.element.allowCustomValue) && (
                <div className="form-group">
                  <label className="control-label" htmlFor="custom-value-type">
                    Custom value input type
                  </label>
                  <select
                    id="custom-value-type"
                    className="form-control"
                    value={this.props.element.customValueType || 'text'}
                    onChange={this.editElementProp.bind(
                      this,
                      'customValueType',
                      'value',
                    )}
                  >
                    <option value="text">Text</option>
                    <option value="number">Number</option>
                    <option value="url">URL</option>
                    <option value="email">Email</option>
                    <option value="tel">Phone</option>
                    <option value="date">Date</option>
                    <option value="datetime-local">Date &amp; time</option>
                    <option value="time">Time</option>
                    <option value="textarea">Multi-line text</option>
                  </select>
                  <small className="form-text text-muted">
                    What kind of input the custom-value box shows.
                  </small>
                </div>
              )}
            {this.props.element.key === 'ChipSelect' && (
              <div className="form-group">
                <label className="control-label" htmlFor="chip-select-layout">
                  Layout
                </label>
                <select
                  id="chip-select-layout"
                  className="form-control"
                  value={this.props.element.layout || 'chips'}
                  onChange={this.editElementProp.bind(this, 'layout', 'value')}
                >
                  {CHIP_SELECT_LAYOUTS.map((layout) => (
                    <option key={layout.value} value={layout.value}>
                      {layout.label}
                    </option>
                  ))}
                </select>
                <small className="form-text text-muted">
                  List and Cards show large tappable answers and can show an
                  image per option.
                </small>
              </div>
            )}
            {this.props.element.key === 'ChipSelect' &&
              canAutoAdvance(this.props.element) && (
                <div className="form-group">
                  <div className="custom-control custom-checkbox">
                    <input
                      id="auto-advance"
                      className="custom-control-input"
                      type="checkbox"
                      checked={Boolean(this.props.element.autoAdvance)}
                      onChange={this.editElementProp.bind(
                        this,
                        'autoAdvance',
                        'checked',
                      )}
                    />
                    <label className="custom-control-label" htmlFor="auto-advance">
                      Go to the next step when an option is picked
                    </label>
                  </div>
                  <small className="form-text text-muted">
                    Multi-step forms only. Works best when this is the only
                    question on its step; the last step still needs a Submit
                    button.
                  </small>
                </div>
              )}
            {this.props.element.hasOwnProperty(
              'readOnly',
            ) && (
              <div className="custom-control custom-checkbox">
                <input
                  id="is-read-only"
                  className="custom-control-input"
                  type="checkbox"
                  checked={this_read_only}
                  onChange={this.editElementProp.bind(
                    this,
                    'readOnly',
                    'checked',
                  )}
                />
                <label
                  className="custom-control-label"
                  htmlFor="is-read-only"
                >
                  <IntlMessages id="read-only" />
                </label>
              </div>
            )}
            {this.props.element.hasOwnProperty(
              'defaultToday',
            ) && (
              <div className="custom-control custom-checkbox">
                <input
                  id="is-default-to-today"
                  className="custom-control-input"
                  type="checkbox"
                  checked={this_default_today}
                  onChange={this.editElementProp.bind(
                    this,
                    'defaultToday',
                    'checked',
                  )}
                />
                <label
                  className="custom-control-label"
                  htmlFor="is-default-to-today"
                >
                  <IntlMessages id="default-to-today" />?
                </label>
              </div>
            )}
            {this.props.element.hasOwnProperty(
              'showTimeSelect',
            ) && (
              <div className="custom-control custom-checkbox">
                <input
                  id="show-time-select"
                  className="custom-control-input"
                  type="checkbox"
                  checked={this_show_time_select}
                  onChange={this.editElementProp.bind(
                    this,
                    'showTimeSelect',
                    'checked',
                  )}
                />
                <label
                  className="custom-control-label"
                  htmlFor="show-time-select"
                >
                  <IntlMessages id="show-time-select" />?
                </label>
              </div>
            )}
            {this_show_time_select &&
              this.props.element.hasOwnProperty(
                'showTimeSelectOnly',
              ) && (
                <div className="custom-control custom-checkbox">
                  <input
                    id="show-time-select-only"
                    className="custom-control-input"
                    type="checkbox"
                    checked={this_show_time_select_only}
                    onChange={this.editElementProp.bind(
                      this,
                      'showTimeSelectOnly',
                      'checked',
                    )}
                  />
                  <label
                    className="custom-control-label"
                    htmlFor="show-time-select-only"
                  >
                    <IntlMessages id="show-time-select-only" />
                    ?
                  </label>
                </div>
              )}
            {this.props.element.hasOwnProperty(
              'showTimeInput',
            ) && (
              <div className="custom-control custom-checkbox">
                <input
                  id="show-time-input"
                  className="custom-control-input"
                  type="checkbox"
                  checked={this_show_time_input}
                  onChange={this.editElementProp.bind(
                    this,
                    'showTimeInput',
                    'checked',
                  )}
                />
                <label
                  className="custom-control-label"
                  htmlFor="show-time-input"
                >
                  <IntlMessages id="show-time-input" />?
                </label>
              </div>
            )}

            {(this.state.element?.element ===
              'RadioButtons' ||
              this.state.element?.element ===
                'Checkboxes') &&
              canHaveDisplayHorizontal && (
                <div className="custom-control custom-checkbox">
                  <input
                    id="display-horizontal"
                    className="custom-control-input"
                    type="checkbox"
                    checked={this_checked_inline}
                    onChange={this.editElementProp.bind(
                      this,
                      'inline',
                      'checked',
                    )}
                  />
                  <label
                    className="custom-control-label"
                    htmlFor="display-horizontal"
                  >
                    <IntlMessages id="display-horizontal" />
                  </label>
                </div>
              )}
          </div>
        )}
        {isSlotBooking && (
          <SlotBookingEditor
            value={this.state.element?.slotConfig ?? this.props.element?.slotConfig}
            onChange={(slotConfig) => {
              const this_element = this.state.element;
              this_element.slotConfig = slotConfig;
              this.setState({ element: this_element, dirty: true }, () =>
                this.updateElement(),
              );
            }}
          />
        )}
        {isQuizBlock && (
          <QuizBlockEditor
            blockKey={this.props.element.key}
            value={this.state.element?.blockConfig ?? this.props.element?.blockConfig}
            onChange={(blockConfig) => {
              const this_element = this.state.element;
              this_element.blockConfig = blockConfig;
              this.setState({ element: this_element, dirty: true }, () =>
                this.updateElement(),
              );
            }}
          />
        )}
        {isScaleBlock && (
          <ScaleBlockEditor
            blockKey={this.props.element.key}
            value={this.state.element?.blockConfig ?? this.props.element?.blockConfig}
            onChange={(blockConfig) => {
              const this_element = this.state.element;
              this_element.blockConfig = blockConfig;
              this.setState({ element: this_element, dirty: true }, () =>
                this.updateElement(),
              );
            }}
          />
        )}
        {this.props.element.hasOwnProperty('src') && (
          <div className="form-group">
            <label
              className="control-label"
              htmlFor="srcInput"
            >
              Image URL
            </label>
            <input
              id="srcInput"
              type="text"
              className="form-control"
              value={element.src || ''}
              onBlur={this.handleBlur}
              onChange={(e) =>
                this.editElementProp('src', 'value', e)
              }
            />
          </div>
        )}
        {canHaveImageSize && (
          <div>
            {' '}
            <div className="form-group">
              <div className="custom-control custom-checkbox">
                <input
                  id="do-center"
                  className="custom-control-input"
                  type="checkbox"
                  checked={this_checked_center}
                  onChange={this.editElementProp.bind(
                    this,
                    'center',
                    'checked',
                  )}
                />
                <label
                  className="custom-control-label"
                  htmlFor="do-center"
                >
                  <IntlMessages id="center" />?
                </label>
              </div>
            </div>
          </div>
        )}
        {canHaveImageSize && (
          <div>
            <div className="row">
              <div className="col-sm-3">
                <label
                  className="control-label"
                  htmlFor="elementWidth"
                >
                  <IntlMessages id="width" />:
                </label>
                <input
                  id="elementWidth"
                  type="text"
                  className="form-control"
                  defaultValue={element.width || ''}
                  onBlur={this.handleBlur}
                  onChange={this.editElementProp.bind(
                    this,
                    'width',
                    'value',
                  )}
                />
              </div>
              <div className="col-sm-3">
                <label
                  className="control-label"
                  htmlFor="elementHeight"
                >
                  <IntlMessages id="height" />:
                </label>
                <input
                  id="elementHeight"
                  type="text"
                  className="form-control"
                  defaultValue={element.height || ''}
                  onBlur={this.handleBlur}
                  onChange={this.editElementProp.bind(
                    this,
                    'height',
                    'value',
                  )}
                />
              </div>
            </div>
          </div>
        )}

        {this.state.element?.element === 'FileUpload' && (
          <div>
            <div className="form-group">
              <label
                className="control-label"
                htmlFor="fileType"
              >
                <IntlMessages id="choose-file-type" />:
              </label>
              <select
                id="fileType"
                className="form-control"
                onBlur={this.updateElement.bind(this)}
                onChange={this.editElementProp.bind(
                  this,
                  'fileType',
                  'value',
                )}
              >
                {[
                  {
                    type: 'image, application/pdf, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-powerpoint, application/vnd.openxmlformats-officedocument.presentationml.presentation, video/mp4,video/x-m4v,video/*',
                    typeName: 'All File Type',
                  },
                  { type: 'image', typeName: 'Image' },
                  {
                    type: 'application/pdf',
                    typeName: 'PDF',
                  },
                  {
                    type: 'application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                    typeName: 'Word',
                  },
                  {
                    type: 'application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                    typeName: 'Excel',
                  },
                  {
                    type: 'application/vnd.ms-powerpoint, application/vnd.openxmlformats-officedocument.presentationml.presentation',
                    typeName: 'Powerpoint',
                  },
                  {
                    type: 'video/mp4,video/x-m4v,video/*',
                    typeName: 'Videos',
                  },
                ].map(
                  (file: FileTypeOption, index: number) => (
                    <option value={file.type} key={index}>
                      {file.typeName}
                    </option>
                  ),
                )}
              </select>
            </div>
          </div>
        )}
        {this.state.element?.element === 'Signature' &&
        this.props.element.readOnly ? (
          <div className="form-group">
            <label
              className="control-label"
              htmlFor="variableKey"
            >
              <IntlMessages id="variable-key" />:
            </label>
            <input
              id="variableKey"
              type="text"
              className="form-control"
              defaultValue={this.props.element.variableKey}
              onBlur={this.updateElement.bind(this)}
              onChange={this.editElementProp.bind(
                this,
                'variableKey',
                'value',
              )}
            />
            <p className="help-block">
              <IntlMessages id="variable-key-desc" />.
            </p>
          </div>
        ) : (
          <div />
        )}
        {canHavePageBreakBefore && (
          <div className="form-group">
            <label className="control-label">
              <IntlMessages id="print-options" />
            </label>
            <div className="custom-control custom-checkbox">
              <input
                id="page-break-before-element"
                className="custom-control-input"
                type="checkbox"
                checked={this_checked_page_break}
                onChange={this.editElementProp.bind(
                  this,
                  'pageBreakBefore',
                  'checked',
                )}
              />
              <label
                className="custom-control-label"
                htmlFor="page-break-before-element"
              >
                <IntlMessages id="page-break-before-elements" />
                ?
              </label>
            </div>
          </div>
        )}


        {this.props.element.hasOwnProperty('min_value') && (
          <div className="form-group">
            <div className="form-group-range">
              <label
                className="control-label"
                htmlFor="rangeMin"
              >
                <IntlMessages id="min" />
              </label>
              <input
                id="rangeMin"
                type="number"
                className="form-control"
                defaultValue={this.props.element.min_value}
                onBlur={this.updateElement.bind(this)}
                onChange={this.editElementProp.bind(
                  this,
                  'min_value',
                  'value',
                )}
              />
              <input
                type="text"
                className="form-control"
                defaultValue={this.props.element.min_label}
                onBlur={this.updateElement.bind(this)}
                onChange={this.editElementProp.bind(
                  this,
                  'min_label',
                  'value',
                )}
              />
            </div>
          </div>
        )}
        {this.props.element.hasOwnProperty('max_value') && (
          <div className="form-group">
            <div className="form-group-range">
              <label
                className="control-label"
                htmlFor="rangeMax"
              >
                <IntlMessages id="max" />
              </label>
              <input
                id="rangeMax"
                type="number"
                className="form-control"
                defaultValue={this.props.element.max_value}
                onBlur={this.updateElement.bind(this)}
                onChange={this.editElementProp.bind(
                  this,
                  'max_value',
                  'value',
                )}
              />
              <input
                type="text"
                className="form-control"
                defaultValue={this.props.element.max_label}
                onBlur={this.updateElement.bind(this)}
                onChange={this.editElementProp.bind(
                  this,
                  'max_label',
                  'value',
                )}
              />
            </div>
          </div>
        )}
        {this.props.element.hasOwnProperty(
          'default_value',
        ) && (
          <div className="form-group">
            <div className="form-group-range">
              <label
                className="control-label"
                htmlFor="defaultSelected"
              >
                <IntlMessages id="default-selected" />
              </label>
              <input
                id="defaultSelected"
                type="number"
                className="form-control"
                defaultValue={
                  this.props.element.default_value
                }
                onBlur={this.updateElement.bind(this)}
                onChange={this.editElementProp.bind(
                  this,
                  'default_value',
                  'value',
                )}
              />
            </div>
          </div>
        )}
        {this.props.element.showDescription && (
          <div className="form-group">
            <label
              className="control-label"
              htmlFor="questionDescription"
            >
              <IntlMessages id="description" />
            </label>
            <TextAreaAutosize
              className="form-control"
              id="questionDescription"
              defaultValue={this.props.element.description}
              onBlur={this.updateElement.bind(this)}
              onChange={this.editElementProp.bind(
                this,
                'description',
                'value',
              )}
            />
          </div>
        )}
        {this.props.showCorrectColumn &&
          this.props.element.canHaveAnswer &&
          !this.props.element.hasOwnProperty('options') && (
            <div className="form-group">
              <label
                className="control-label"
                htmlFor="correctAnswer"
              >
                <IntlMessages id="correct-answer" />
              </label>
              <input
                id="correctAnswer"
                type="text"
                className="form-control"
                defaultValue={this.props.element.correct}
                onBlur={this.updateElement.bind(this)}
                onChange={this.editElementProp.bind(
                  this,
                  'correct',
                  'value',
                )}
              />
            </div>
          )}
        {this.props.element.canPopulateFromApi &&
          this.props.element.hasOwnProperty('options') && (
            <div className="form-group">
              <label
                className="control-label"
                htmlFor="optionsApiUrl"
              >
                <IntlMessages id="populate-options-from-api" />
              </label>
              <input
                className="form-control"
                type="text"
                id="optionsApiUrl"
                placeholder="http://localhost:8080/api/optionsdata"
              />
              <small className="form-text text-muted">
                The endpoint must return a JSON array shaped like:
              </small>
              <pre
                style={{
                  margin: '6px 0 10px',
                  padding: '8px 10px',
                  background: ADMIN_COLORS.headBg,
                  border: `1px solid ${ADMIN_COLORS.border}`,
                  borderRadius: 6,
                  fontSize: 12,
                  lineHeight: 1.5,
                  color: ADMIN_COLORS.textSecondary,
                  overflowX: 'auto',
                }}
              >
{`[
  { "text": "Option 1", "value": "option_1" },
  { "text": "Option 2", "value": "option_2" }
]`}
              </pre>
              <button
                onClick={this.addOptions.bind(this)}
                type="button"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '7px 16px',
                  border: 'none',
                  borderRadius: 6,
                  background: ADMIN_COLORS.accent,
                  color: '#fff',
                  fontSize: 13.5,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = ADMIN_COLORS.accentHover;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = ADMIN_COLORS.accent;
                }}
              >
                <IntlMessages id="populate" />
              </button>
            </div>
          )}
   
        {this.props.element.hasOwnProperty('options') &&
          RICH_OPTION_ELEMENTS.includes(this.props.element.key) && (
            <MultiSelectOptionsEditor
              showImages={layoutShowsImages(element.layout)}
              showExclusive={Boolean(element.multiple)}
              updateElement={this.props.updateElement}
              preview={this.props.preview}
              element={element}
              key={this.state.optionsVersion || 0}
            />
          )}
        {this.props.element.hasOwnProperty('options') &&
          !RICH_OPTION_ELEMENTS.includes(this.props.element.key) && (
          <DynamicOptionList
            showCorrectColumn={this.props.showCorrectColumn}
            canHaveOptionCorrect={canHaveOptionCorrect}
            canHaveOptionValue={canHaveOptionValue}
            data={this.props.preview?.state?.data || []}
            updateElement={this.props.updateElement}
            preview={this.props.preview}
            element={element}
            key={this.state.optionsVersion || 0}
          />
        )}
      </div>
    );
  }
}
